/** @jsxRuntime classic */
/** @jsx jsx */
// eslint-disable-next-line no-restricted-imports
import { css, jsx } from "@emotion/core";
import * as React from "react";
import { Container, DragIndicatorVerticalIcon, PositionedOverlay, Theme, TrashIcon } from "@ice/components";
import { SketchPicker } from "react-color";


/*
	Single color square used to either display, select or edit a color.
	If in edit mode it has additional elements, like trashcan and DnD feature.
 */

interface SingleColorChooserProps {
	color: string;
	editMode?: boolean;
	onColorChanged?: (color: string) => void;
	isEdited?: boolean;
	simpleSelect?: boolean;
	onSelect?: (color: string) => void;
	onEditFinished?: () => void;
	onDelete?: () => void;
}

interface SingleColorChooserState {
	color: string;
	editing: boolean;
}

class SingleColorChooser extends React.Component<SingleColorChooserProps, SingleColorChooserState> {

	constructor(props: SingleColorChooserProps) {
		super(props);
		this.state = {
			color: this.props.color,
			editing: this.props.isEdited
		};
	}

	componentDidMount() {
		if (this.props.isEdited) {
			this.setState({editing: true});
		}
	}

	private renderColorSquare = () => {
		return (
			<div
				style={{
					backgroundColor: this.state.color,
					border: "rgba(128, 128, 128, 0.3) 1px solid",
					width: "1.5em",
					height: "1.5em",
					marginRight: "0.4em",
					borderRadius: "20%",
					cursor: "pointer",
				}}
				onClick={() => {
					if (this.props.simpleSelect) {
						this.setState({editing: !this.state.editing});
					} else if (this.props.onSelect) {
						this.props.onSelect(this.state.color);
					}
				}}
			/>
		);
	};

	private divRef = React.createRef<HTMLDivElement>();

	render() {
		if (this.props.editMode) {
			return (
				<div
					key={`k_${this.props.color}_${this.state.editing}`}
					ref={this.divRef}
					css={(theme: Theme) => css`
					  	display: flex;
					  	flex-direction: row;
					  	align-items: center;
					  	padding: 0.4rem 0;
					  	cursor: pointer;
					  	border: ${theme.button.borderColor} 1px solid;
					  	border-radius: 0.2rem;
					  	margin-right: 0.4rem;
					  	background-color: ${this.props.isEdited ? theme.menu.active.backgroundColor : theme.backgroundColor.default};
					`}
				>
					<DragIndicatorVerticalIcon width="fixed" style={{ cursor: "grabbing" }} />
					{this.renderColorSquare()}
					{this.props.onDelete && <TrashIcon width="fixed" onClick={() => this.props.onDelete()} />}
					{this.props.isEdited && (
						<PositionedOverlay
							closeOnClickOutside
							closeOnEscapeKeyPress
							isInside={(t) => this.divRef?.current?.contains(t)}
							placement={"bottom-start"}
							target={this.divRef.current}
							onClose={() => this.props.onEditFinished && this.props.onEditFinished()}>
							{(getProps) => (
								<Container {...getProps()}>
									<SketchPicker disableAlpha color={this.state.color} onChange={(c) => this.props.onColorChanged && this.props.onColorChanged(c.hex)} />
								</Container>
							)}
						</PositionedOverlay>
					)}
				</div>
			);
		} else if (this.props.simpleSelect) {
			return (
				<div ref={this.divRef} style={{ display: "flex", flexDirection: "row"}}>
					{this.renderColorSquare()}
					{this.state.editing && (
						<PositionedOverlay
							closeOnClickOutside
							closeOnEscapeKeyPress
							isInside={(t) => this.divRef?.current?.contains(t)}
							placement={"bottom-start"}
							target={this.divRef.current}
							onClose={() => {
								this.setState({editing: false});
								this.props.onEditFinished && this.props.onEditFinished();
							}}>
							{(getProps) => (
								<Container {...getProps()}>
									<SketchPicker disableAlpha color={this.state.color} onChange={(c) => {
										this.setState({color: c.hex});
										this.props.onColorChanged && this.props.onColorChanged(c.hex)
									}} />
								</Container>
							)}
						</PositionedOverlay>
					)}
				</div>
			);
		} else {
			return this.renderColorSquare();
		}
	}
}

export default SingleColorChooser;
